/**
 * Shared types for the AttachSourceModal and its tabs.
 *
 * The modal is presentational - it owns per-tab form state and validation
 * but performs no API calls. Each tab's submission produces a `StagedSource`
 * which the caller's `onAdd` handler decides what to do with (stage locally
 * for home-page lesson creation, or fire a mutation hook in-lesson).
 */

export type StagedSource =
  | { kind: 'file'; file: File }
  | { kind: 'link'; url: string; name?: string }
  | { kind: 'text'; text: string; name: string; description?: string };

export type AttachTabId = 'file' | 'link' | 'text';

export interface AttachSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called once per staged source. For 'file' the modal calls onAdd
   * once per selected File. Returning a Promise lets the modal show a
   * busy state and disable Submit while in flight; throw to keep the
   * modal open and surface the error inline.
   */
  onAdd: (source: StagedSource) => void | Promise<void>;
  /**
   * Optional: when a user clicks the X on a session-log chip, the modal
   * removes it from its own log AND emits this callback so the caller
   * can drop the source from its own state (home-page staging arrays,
   * or in-lesson API delete). If omitted, the modal still removes from
   * its own log but the caller's state is untouched.
   */
  onRemove?: (kind: AttachTabId, indexInKind: number) => void;
  /** Hard cap for the file tab. Home page passes MAX_FILES - files.length. */
  remainingFileSlots?: number;
  /** Default tab when opened. Defaults to 'file'. */
  initialTab?: AttachTabId;
}

/**
 * Grouped view of supported formats - drives the OS file picker `accept`
 * attribute, client-side rejection, AND the "Supported file types" modal
 * surfaced from the upload tab.
 *
 * Sourced from Reducto's documented supported-file-formats set
 * (docs.reducto.ai/parsing/supported-file-formats, verified 2026-05). Audio
 * and video are not supported by Reducto. HTML / email / OpenDocument /
 * ebook formats are also not in Reducto's accepted list and have been
 * removed - backend hard-rejects them at upload.
 */
export const FORMAT_CATEGORIES: readonly {
  label: string;
  description: string;
  exts: readonly string[];
}[] = [
  {
    label: 'Documents',
    description: 'PDF, Word, WordPerfect, plain text',
    exts: ['pdf', 'doc', 'docx', 'dotx', 'rtf', 'txt', 'wpd'],
  },
  {
    label: 'Slides',
    description: 'PowerPoint',
    exts: ['ppt', 'pptx'],
  },
  {
    label: 'Spreadsheets',
    description: 'Excel and CSV',
    exts: ['xlsx', 'xls', 'xlsm', 'xltx', 'xltm', 'csv', 'qpw'],
  },
  {
    label: 'Images',
    description: 'Text extracted via OCR',
    exts: [
      'png',
      'jpg',
      'jpeg',
      'gif',
      'bmp',
      'tif',
      'tiff',
      'heic',
      'heif',
      'psd',
      'pcx',
      'ppm',
      'apng',
      'cur',
      'dcx',
      'ftex',
      'pixar',
    ],
  },
] as const;

/**
 * Flat union of all supported extensions, with leading dots - used for
 * the file picker's `accept` attribute and client-side filtering. Derived
 * from FORMAT_CATEGORIES so the two never drift.
 */
export const SUPPORTED_FILE_EXTENSIONS: readonly string[] =
  FORMAT_CATEGORIES.flatMap(c => c.exts.map(e => `.${e}`));

export const ACCEPT_ATTR = SUPPORTED_FILE_EXTENSIONS.join(',');

/** Per-file size cap (MB) enforced client-side. Backend should match. */
export const MAX_FILE_SIZE_MB = 50;

/** Max chars for a pasted-text source - backend hard-validates the same. */
export const MAX_TEXT_CHARS = 20_000;

/** Max chars for a text source title - backend hard-validates the same. */
export const MAX_TEXT_TITLE_CHARS = 200;

export function isSupportedFile(name: string): boolean {
  const lower = name.toLowerCase();
  return SUPPORTED_FILE_EXTENSIONS.some(ext => lower.endsWith(ext));
}
